import { Card, Typography } from "@material-ui/core";
import { removeTokenCookies } from "@saleor/auth";
import useAppState from "@saleor/hooks/useAppState";
import { defaultDataIdFromObject, InMemoryCache } from "apollo-cache-inmemory";
import { ApolloClient } from "apollo-client";
import { ApolloLink } from "apollo-link";
import { BatchHttpLink } from "apollo-link-batch-http";
import { createUploadLink } from "apollo-upload-client";
import React, { Fragment } from "react";
import { ApolloProvider } from "react-apollo";
import { render } from "react-dom";
import ErrorBoundary from "react-error-boundary";
import TagManager from "react-gtm-module";
import { useIntl } from "react-intl";
import {
  BrowserRouter,
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";

import AccountSettingSection from "./accountSettings";
import { accountSetting } from "./accountSettings/urls";
import AnnouncementsSection from "./announcements";
import AppConfigurationSection from "./appConfiguration";
import AppsSection from "./apps";
import { appsSection } from "./apps/urls";
import AttributeSection from "./attributes";
import { attributeSection } from "./attributes/urls";
import AuditLogs from "./auditLogs";
import Auth from "./auth";
import AuthProvider, { useAuth } from "./auth/AuthProvider";
import LoginLoading from "./auth/components/LoginLoading/LoginLoading";
import authLink from "./auth/link";
import { hasPermission } from "./auth/misc";
import SavedAccountProvider from "./auth/SavedAccountProvider";
import { switchAccountPath } from "./auth/urls";
import SwitchAccountView from "./auth/views/SwitchAccount";
import ExpressFleetList from "./autobooking";
import ExpressFleetOpsPage from "./autobooking/views/ExpressFleetOpsPage";
import BannerSection from "./banners";
import { BrandsSection } from "./brands";
import BusinessDetails from "./businessdtlsform";
import CategorySection from "./categories";
import ClusterSection from "./cluster";
import CollectionSection from "./collections";
import AppLayout from "./components/AppLayout";
import CustomHeader from "./components/CustomHeader";
import { DateProvider } from "./components/Date";
import { LocaleProvider } from "./components/Locale";
import MessageManagerProvider from "./components/messages";
import { ShopProvider } from "./components/Shop";
import ThemeProvider from "./components/Theme";
import { API_URI, APP_MOUNT_URI, BOSS_URI, GTM_ID } from "./config";
import ConfigurationSection, { createConfigurationMenu } from "./configuration";
import ConfigurationOpsSection, {
  createConfigurationOpsMenu
} from "./configuration_operations";
import AppStateProvider from "./containers/AppState";
import BackgroundTasksProvider from "./containers/BackgroundTasks";
import CouponsSection from "./coupons";
import { CustomerSection } from "./customers";
import DiscountSection from "./discounts";
import DispatchSection from "./dispatch";
import DropOffSection from "./dropOff";
import HomePage from "./home";
import { useRumBrowser } from "./hooks/useRumBrowser";
import MallSection from "./malls";
import MasterFulfillment from "./masterFulfillment";
import OrdersSection from "./motherOrders";
import NavigationSection from "./navigation";
import { navigationSection } from "./navigation/urls";
import { NotFound } from "./NotFound";
import PageSection from "./pages";
import PaymentEventsListSection from "./paymentEvents";
import PermissionGroupSection from "./permissionGroups";
import PluginsSection from "./plugins";
import ProductSection from "./products";
import ProductTypesSection from "./productTypes";
import RemittanceSection from "./remittance";
import RemittanceRunnerSection from "./remittanceRunner";
import BatchSection from "./reports/batch";
import BatchHistorySection from "./reports/batchHistory";
import BrandVerticalsOmi from "./reports/brandVerticalsOmi";
import CustomerActivityReportSection from "./reports/customerActivity/";
import CustomerInfoSection from "./reports/customerInfo";
import GrabExpress from "./reports/grabExpress";
import MallOrdersBiReportSection from "./reports/mallOrders";
import MerchantSettlementSection from "./reports/merchantSettlement";
import OrderLinesSection from "./reports/orderLines";
import OrderLinesFinanceSection from "./reports/orderLinesFinance";
import ReportsSection from "./reports/overview";
import PayoutOrdersSection from "./reports/PayoutOrders";
import PayoutsSection from "./reports/payouts";
import ProductCategoriesBiReportSection from "./reports/productCategoriesBi/";
import ProductDetailsBiReportSection from "./reports/productDetailsBi/";
import ProductReportsSection from "./reports/products";
import ProductStocksReportSection from "./reports/productStocks";
import StoreDetailsFinanceSection from "./reports/storeDetailsFinance";
import StoreOrdersBiSection from "./reports/storeOrdersBi";
import StoreOrdersFinanceSection from "./reports/storeOrdersFinance";
import StoresReportSection from "./reports/stores";
import TransportReportSection from "./reports/transportReport";
import VoucherReportSection from "./reports/voucher/";
import RemittanceConfig from "./riderRemittanceConfig";
import SellerSection from "./seller";
import SellerOrdersListSection from "./sellerOrders";
import ShippingSection from "./shipping";
import SiteSettingsSection from "./siteSettings";
import StaffSection from "./staff";
import StoreSection from "./store";
import StoreOrderSection from "./storeOrders";
import TaxesSection from "./taxes";
import TranslationsSection from "./translations";
import { PermissionEnum } from "./types/globalTypes";
import WarehouseSection from "./warehouses";
import { warehouseSection } from "./warehouses/urls";

if (process.env.GTM_ID !== undefined) {
  TagManager.initialize({ gtmId: GTM_ID });
}
// DON'T TOUCH THIS
// These are separate clients and do not share configs between themselves
// so we need to explicitly set them
const linkOptions = {
  credentials: "include",
  uri: API_URI
};
const uploadLink = createUploadLink(linkOptions);
const batchLink = new BatchHttpLink({
  batchInterval: 100,
  ...linkOptions
});

const bossLink = {
  uri: BOSS_URI
};
const bossBatchLink = new BatchHttpLink({
  batchInterval: 100,
  ...bossLink
});

const bossAndUploadLink = ApolloLink.split(
  operation => operation.getContext().useBoss,
  bossBatchLink,
  uploadLink
);

const link = ApolloLink.split(
  operation => operation.getContext().useBatching,
  batchLink,
  bossAndUploadLink
);

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    dataIdFromObject: (obj: any) => {
      // We need to set manually shop's ID, since it is singleton and
      // API does not return its ID
      if (obj.__typename === "Shop") {
        return "shop";
      } else if (
        obj.__typename === "ActiveBatchDetailsReportEdge" ||
        "activeBatchDetailsReport"
      ) {
        // return null disables apollo built-in data normalization on a type basis
        return null;
      }
      return defaultDataIdFromObject(obj);
    }
  }),
  link: authLink.concat(link)
});

const App: React.FC = () => {
  const isDark = localStorage.getItem("theme") === "true";

  useRumBrowser();
  return (
    <ApolloProvider client={apolloClient}>
      <BrowserRouter basename={APP_MOUNT_URI}>
        <ThemeProvider isDefaultDark={isDark}>
          <DateProvider>
            <LocaleProvider>
              <MessageManagerProvider>
                <BackgroundTasksProvider>
                  <AppStateProvider>
                    <ShopProvider>
                      <SavedAccountProvider>
                        <AuthProvider>
                          <AppRoutes />
                        </AuthProvider>
                      </SavedAccountProvider>
                    </ShopProvider>
                  </AppStateProvider>
                </BackgroundTasksProvider>
              </MessageManagerProvider>
            </LocaleProvider>
          </DateProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ApolloProvider>
  );
};

const AppRoutes: React.FC = () => {
  const intl = useIntl();
  const [, dispatchAppState] = useAppState();
  const { pathname } = useLocation();
  const useNavigateState = useNavigate();
  const {
    hasToken,
    isAuthenticated,
    tokenAuthLoading,
    tokenVerifyLoading,
    user
  } = useAuth();

  if (pathname === switchAccountPath) {
    return (
      <Routes>
        <Route path={switchAccountPath} element={<SwitchAccountView />} />
      </Routes>
    );
  }

  const handleMfaRedirect = () => {
    removeTokenCookies();
    const mfaLocalStorage = JSON.parse(
      localStorage.getItem("mfaLocalStorage") || "{}"
    );
    localStorage.setItem("mfaLocalStorage", JSON.stringify(mfaLocalStorage));
    useNavigateState("/mfa-verification-page/", {
      state: mfaLocalStorage
    });
    window.location.reload();
  };

  return (
    <>
      {isAuthenticated && !tokenAuthLoading && !tokenVerifyLoading ? (
        <Fragment>
          <CustomHeader />
          {user?.mfaRemainingGraceDays !== 0 &&
          user.permissionGroups.some(
            permission =>
              permission.mfaEnabledDate !== null &&
              ["OMI Admin", "Ops Master", "OMI Ops"].includes(permission.name)
          ) ? (
            <Card
              style={{
                alignItems: "center",
                backgroundColor: "#FEF7B4",
                display: "flex",
                height: 45,
                justifyContent: "center",
                marginTop: 80,
                position: "fixed",
                textAlign: "center",
                width: "100%"
              }}
            >
              <Typography style={{ fontSize: 12, textAlign: "center" }}>
                Better protect your information by activating{" "}
                <b>
                  <Link
                    to="#"
                    onClick={handleMfaRedirect}
                    style={{ textDecoration: "none" }}
                  >
                    <u>two-factor authentication now.</u>
                  </Link>
                </b>{" "}
                After{" "}
                <b>{user.permissionGroups[0]?.mfaRemainingGraceDays} days,</b>{" "}
                this will be mandatory.
              </Typography>
            </Card>
          ) : null}

          <AppLayout>
            {user?.mfaRemainingGraceDays !== 0 &&
            user.permissionGroups.some(
              permission =>
                permission.mfaEnabledDate !== null &&
                ["OMI Admin", "OPS Master", "OMI Ops"].includes(permission.name)
            ) ? (
              <div
                style={{
                  marginTop: 60
                }}
              >
                {" "}
              </div>
            ) : null}

            <ErrorBoundary
              onError={() =>
                dispatchAppState({
                  payload: {
                    error: "unhandled"
                  },
                  type: "displayError"
                })
              }
            >
              <Routes>
                <Route path="/mfa-auth-page" element={<HomePage />} />
                <Route path="/" element={<HomePage />} />
                <Route
                  path="/announcements/*"
                  element={<AnnouncementsSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_BANNERS]}
                  path="/discounts/banners/*"
                  element={<BannerSection />}
                />
                // Possibly no longer in use
                <Route
                  handle={[PermissionEnum.MANAGE_PRODUCTS]}
                  path="/business-details/*"
                  element={<BusinessDetails />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_CATEGORIES]}
                  path="/categories/*"
                  element={<CategorySection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_CLUSTERS]}
                  path="/clusters/*"
                  element={<ClusterSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_COLLECTIONS]}
                  path="/collections/*"
                  element={<CollectionSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_USERS]}
                  path="/customers/*"
                  element={<CustomerSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_APP_CONFIGS]}
                  path="/configuration/appConfiguration/*"
                  element={<AppConfigurationSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_SALES]}
                  path="/discounts/*"
                  element={<DiscountSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_PAGES]}
                  path="/configuration/pages/*"
                  element={<PageSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_PLUGINS]}
                  path="/configuration/plugins/*"
                  element={<PluginsSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_PRODUCTS]}
                  path="/products/*"
                  element={<ProductSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES]}
                  path="/configuration/product-types/*"
                  element={<ProductTypesSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_STAFF]}
                  path="/staff/*"
                  element={<StaffSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_STAFF]}
                  path="/permission-groups/*"
                  element={<PermissionGroupSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_SETTINGS]}
                  path="/configuration/site-settings/*"
                  element={<SiteSettingsSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_APP_CONFIGS]}
                  path="/appConfiguration/*"
                  element={<AppConfigurationSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_SETTINGS]}
                  path="/configuration/taxes/*"
                  element={<TaxesSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_SHIPPING]}
                  path="/configuration/shipping/*"
                  element={<ShippingSection />}
                />
                // Possibly no longer in use
                <Route
                  handle={[PermissionEnum.MANAGE_TRANSLATIONS]}
                  path="/translations/*"
                  element={<TranslationsSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_MALLS,
                    PermissionEnum.MODIFY_MALLS
                  ]}
                  path="/malls/*"
                  element={<MallSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_TENANTS,
                    PermissionEnum.MODIFY_TENANTS
                  ]}
                  path="/store/*"
                  element={<StoreSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_BRANDS]}
                  path="/brands/*"
                  element={<BrandsSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_BRANDS]}
                  path="/seller/*"
                  element={<SellerSection />}
                />
                <Route
                  handle={[PermissionEnum.VIEW_ADMIN_ORDERS]}
                  path="/store-orders/*"
                  element={<StoreOrderSection />}
                />
                <Route
                  handle={[PermissionEnum.VIEW_ADMIN_FULFILLMENT]}
                  path="/mother-orders/*"
                  element={<OrdersSection />}
                />
                <Route
                  handle={[PermissionEnum.VIEW_MASTER_FULFILLMENT]}
                  path="/master-fulfillment/*"
                  element={<MasterFulfillment />}
                />
                <Route
                  handle={[PermissionEnum.VIEW_ADMIN_DISPATCH]}
                  path="/dispatch/*"
                  element={<DispatchSection />}
                />
                <Route
                  handle={[PermissionEnum.VIEW_ADMIN_DROP_OFF]}
                  path={`drop-off/*`}
                  element={<DropOffSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_REPORTS]}
                  path="/payment-events/*"
                  element={<PaymentEventsListSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_SELLER_ORDER_REPORTS]}
                  path="/seller-Orders/*"
                  element={<SellerOrdersListSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_ACCOUNT_SETTINGS]}
                  path={accountSetting + "/*"}
                  element={<AccountSettingSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_MENUS]}
                  path={navigationSection + "/*"}
                  element={<NavigationSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES]}
                  path={attributeSection + "/*"}
                  element={<AttributeSection />}
                />
                // Possibly no longer in use
                <Route
                  handle={[PermissionEnum.MANAGE_APPS]}
                  path={appsSection + "/*"}
                  element={<AppsSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_PRODUCTS]}
                  path={warehouseSection + "/*"}
                  element={<WarehouseSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_STORE_ORDERS
                  ]}
                  path={"/reports/omi/product-stocks/*"}
                  element={<ProductStocksReportSection />}
                />
                // Possibly no longer in use
                <Route
                  handle={[
                    PermissionEnum.MANAGE_FINANCE,
                    PermissionEnum.MANAGE_PAYOUT,
                    PermissionEnum.GENERATE_PAYABLE_LIST
                  ]}
                  path={"/payouts/*"}
                  element={<PayoutsSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_ORDERS]}
                  path="/remittance/*"
                  element={<RemittanceSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_REMITTANCE]}
                  path="/remittance-runner/*"
                  element={<RemittanceRunnerSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_SETTLEMENT]}
                  path={"/merchant-settlement-report/*"}
                  element={<MerchantSettlementSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_REPORTS]}
                  path={"/reports/omi/grab-express/*"}
                  element={<GrabExpress />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_STORE_ORDERS
                  ]}
                  path={"/reports/omi/store-orders/*"}
                  element={<StoreOrdersBiSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_STORE_ORDERS
                  ]}
                  path={"/reports/omi/order-lines/*"}
                  element={<OrderLinesSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_STORE_ORDERS
                  ]}
                  path={"/reports/omi/transport-report/*"}
                  element={<TransportReportSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_TENANTS
                  ]}
                  path={"/reports/omi/brand-vertical/*"}
                  element={<BrandVerticalsOmi />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_FINANCE
                  ]}
                  path={"/reports/finance/store-orders/*"}
                  element={<StoreOrdersFinanceSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_FINANCE
                  ]}
                  path={"/reports/finance/order-lines/*"}
                  element={<OrderLinesFinanceSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_FINANCE
                  ]}
                  path={"/reports/finance/store/*"}
                  element={<StoreDetailsFinanceSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_TENANTS
                  ]}
                  path={"/reports/omi/stores/*"}
                  element={<StoresReportSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_TENANTS
                  ]}
                  path={"/reports/omi/customer-info/*"}
                  element={<CustomerInfoSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_TENANTS
                  ]}
                  path={"/reports/bi/product-details/*"}
                  element={<ProductDetailsBiReportSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_TENANTS
                  ]}
                  path={"/reports/bi/product-categories/*"}
                  element={<ProductCategoriesBiReportSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_TENANTS
                  ]}
                  path={"/reports/bi/customer-activities/*"}
                  element={<CustomerActivityReportSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_TENANTS
                  ]}
                  path={"/reports/bi/products/*"}
                  element={<ProductReportsSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_TENANTS
                  ]}
                  path={"/reports/bi/voucher/*"}
                  element={<VoucherReportSection />}
                />
                <Route
                  handle={[
                    PermissionEnum.MANAGE_REPORTS,
                    PermissionEnum.MANAGE_TENANTS
                  ]}
                  path={"/reports/bi/mall-orders/*"}
                  element={<MallOrdersBiReportSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_REPORTS]}
                  path={"/reports/*"}
                  element={<ReportsSection />}
                />
                {createConfigurationMenu(intl).filter(menu =>
                  menu.menuItems.map(item =>
                    hasPermission(item.permission, user)
                  )
                ).length > 0 && (
                  <Route
                    path="/configuration/*"
                    element={<ConfigurationSection />}
                  />
                )}
                {createConfigurationOpsMenu(intl).filter(menu =>
                  menu.menuItems.map(item =>
                    hasPermission(item.permission, user)
                  )
                ).length > 0 && (
                  <Route
                    path="/operations/*"
                    element={<ConfigurationOpsSection />}
                  />
                )}
                <Route
                  handle={[
                    PermissionEnum.MANAGE_FINANCE,
                    PermissionEnum.MANAGE_PAYOUT
                  ]}
                  path={"/payout/finance-orders/*"}
                  element={<PayoutOrdersSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_PAYOUT]}
                  path={"/payout/batch/*"}
                  element={<BatchSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_PAYOUT]}
                  path={"/payout/batch-history/*"}
                  element={<BatchHistorySection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_COUPONS]}
                  path={"/coupons/*"}
                  element={<CouponsSection />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_REMITTANCE_CONFIG]}
                  path="/remittance-config/*"
                  element={<RemittanceConfig />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_AUTO_BOOKING]}
                  path="/express-fleet/*"
                  element={<ExpressFleetList />}
                />
                <Route
                  handle={[PermissionEnum.MANAGE_AUTO_BOOKING]}
                  path="/express-fleet-ops/*"
                  element={<ExpressFleetOpsPage />}
                />
                <Route path="/logs/*" element={<AuditLogs />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ErrorBoundary>
          </AppLayout>
        </Fragment>
      ) : hasToken && tokenVerifyLoading ? (
        <LoginLoading />
      ) : (
        <Auth />
      )}
    </>
  );
};

render(<App />, document.querySelector("#dashboard-app"));
